import {
  abortGisMosaicUpload,
  abortGisRasterUpload,
  finalizeGisMosaicUpload,
  finalizeGisRasterUpload,
  initiateGisMosaicUpload,
  initiateGisRasterUpload,
} from '../api/gisRasterApi'
import {
  abortVectorUpload,
  finalizeVectorUpload,
  initiateVectorUpload,
} from '../api/gisVectorApi'
import {
  abortModel3dUpload,
  finalizeModel3dUpload,
  initiateModel3dUpload,
} from '../api/model3dApi'
import { updateOperationalLayer } from '../api/operationalLayerApi'
import { toast } from '../lib/toast'
import { uploadFileToS3 } from '../utils/s3MultipartUploader'
import { uploadTilesConcurrently } from '../utils/s3TileBatchUploader'

const REAL_DEPS = {
  raster: {
    initiate: initiateGisRasterUpload,
    finalize: finalizeGisRasterUpload,
    abort: abortGisRasterUpload,
    upload: uploadFileToS3,
  },
  vector: {
    initiate: initiateVectorUpload,
    finalize: finalizeVectorUpload,
    abort: abortVectorUpload,
    upload: uploadFileToS3,
  },
  model3d: {
    initiate: initiateModel3dUpload,
    finalize: finalizeModel3dUpload,
    abort: abortModel3dUpload,
    upload: uploadFileToS3,
  },
}

// L'événement `gis-<kind>-upload-success` est ce qui déclenche le re-fetch du
// panneau (GISDashboard) : sans lui la couche n'apparaît pas dans la liste.
export function closeUploadSuccess({
  dispatch,
  key,
  kind,
  serverId,
  layerName,
}) {
  toast.success(`Couche « ${layerName || 'importée'} » importée`)
  window.dispatchEvent(
    new CustomEvent(`gis-${kind}-upload-success`, { detail: { serverId } }),
  )
  dispatch({ type: 'REMOVE', key })
}

function buildPayload(kind, file, meta) {
  const common = {
    filename: file.name,
    size: file.size,
    mime:
      file.type ||
      (kind === 'raster' ? 'image/tiff' : 'application/octet-stream'),
    layer_name: meta.layerName,
    description: meta.description || null,
    folder_id: meta.folderId ?? null,
  }
  if (kind === 'raster') return { ...common, raster_type: meta.rasterType }
  const partage = {
    ...common,
    visible_to_users: meta.visibleToUsers ?? null,
    visible_to_groups: meta.visibleToGroups ?? null,
  }
  if (kind !== 'vector') return partage
  // Calque cible et projection imposee : rangés dans import_meta, relayés à ogr2ogr.
  return {
    ...partage,
    source_layer: meta.sourceLayer ?? null,
    source_srs: meta.sourceSrs ?? null,
  }
}

// La liste de retrait part au finalize (gisVectorController la range dans
// import_meta) ; l'ordre est une colonne de la couche, donc un PUT distinct.
function finalizeBody(kind, uploadResult, meta) {
  if (kind !== 'vector' || !meta.droppedProperties?.length) return uploadResult
  return { ...uploadResult, dropped_properties: meta.droppedProperties }
}

async function persistPropertiesOrder(kind, serverId, meta) {
  if (kind !== 'vector' || !meta.propertiesOrder?.length) return
  try {
    await updateOperationalLayer(serverId, {
      properties_order: meta.propertiesOrder,
    })
  } catch (err) {
    // Un ordre non enregistre ne vaut pas de perdre l'import lui-meme.
    console.warn('[gis-upload] properties_order non enregistre:', err.message)
  }
}

export async function runSingleUpload({
  key,
  kind,
  file,
  meta,
  signal,
  dispatch,
  deps,
}) {
  const d = deps || REAL_DEPS[kind]
  let serverId = null
  try {
    const initiateResult = await d.initiate(buildPayload(kind, file, meta))
    // Test positif par nature : chaque nature a SON identifiant serveur dans
    // la réponse d'initiate. `upload_id` (present à côté) est un leurre — un
    // uuid qu'aucune route finalize/abort n'accepte, cf model3dController.js.
    serverId =
      kind === 'vector'
        ? initiateResult.layer_id
        : kind === 'model3d'
          ? initiateResult.model3d_id
          : initiateResult.raster_id
    dispatch({
      type: 'SET_IDS',
      key,
      serverId,
      uploadId: initiateResult.upload_id || null,
    })
    dispatch({ type: 'PHASE', key, phase: 's3', message: 'Envoi…' })

    const uploadResult = await d.upload({
      file,
      initiateResponse: initiateResult,
      signal,
      onProgress: pct => dispatch({ type: 'S3', key, pct }),
    })

    await d.finalize(serverId, finalizeBody(kind, uploadResult, meta))
    await persistPropertiesOrder(kind, serverId, meta)
    dispatch({
      type: 'PHASE',
      key,
      phase: 'processing',
      message: 'Traitement…',
    })
  } catch (err) {
    if (signal?.aborted) return undefined // annulation volontaire : pas une erreur à afficher
    dispatch({ type: 'ERROR', key, error: humanizeError(err) })
    if (serverId != null) {
      try {
        await d.abort(serverId)
      } catch {
        /* best effort */
      }
    }
  }
  return undefined
}

// Annulation serveur best-effort (fire-and-forget) déclenchée par cancelUpload :
// supprime la row pending + abort le multipart S3 sans attendre la réponse.
// Test positif par nature, à quatre branches : un `else` générique aurait
// envoyé l'annulation d'un import 3D sur la route raster — mauvaise cible
// (peut interrompre l'upload raster EN COURS d'un autre utilisateur si son
// id collisionne), et l'import 3D lui-même resterait 'ingesting' avec son
// multipart S3 orphelin, non annoncé.
export function abortServerSide(type, serverId) {
  const abort =
    type === 'mosaic'
      ? abortGisMosaicUpload
      : type === 'vector'
        ? abortVectorUpload
        : type === 'raster'
          ? abortGisRasterUpload
          : type === 'model3d'
            ? abortModel3dUpload
            : null
  if (!abort) {
    console.error(`[gis-upload] abortServerSide got an unknown type: ${type}`)
    return
  }
  Promise.resolve(abort(serverId)).catch(() => {})
}

export function humanizeError(err) {
  const msg =
    err?.response?.data?.message || err?.message || 'Échec de l’upload.'
  if (/crs|coordinate|projection/i.test(msg)) {
    return 'Le fichier n’a pas de système de coordonnées (CRS).'
  }
  return msg
}

const REAL_MOSAIC_DEPS = {
  initiate: initiateGisMosaicUpload,
  finalize: finalizeGisMosaicUpload,
  abort: abortGisMosaicUpload,
  uploadTiles: uploadTilesConcurrently,
}

export async function runMosaicUpload({
  key,
  tiles,
  meta,
  signal,
  dispatch,
  deps,
}) {
  const d = deps || REAL_MOSAIC_DEPS
  let serverId = null
  try {
    const initiateResult = await d.initiate({
      layer_name: meta.layerName,
      raster_type: meta.rasterType,
      description: meta.description || null,
      folder_id: meta.folderId ?? null,
      visible_to_users: meta.visibleToUsers ?? null,
      visible_to_groups: meta.visibleToGroups ?? null,
      tiles: tiles.map(f => ({ filename: f.name, size: f.size })),
    })
    serverId = initiateResult.raster_id
    dispatch({
      type: 'SET_IDS',
      key,
      serverId,
      uploadId: initiateResult.upload_id || null,
    })
    dispatch({ type: 'PHASE', key, phase: 's3', message: 'Envoi des tuiles…' })

    const presignedByName = {}
    for (const p of initiateResult.presigned) presignedByName[p.filename] = p

    const { failed } = await d.uploadTiles({
      tiles,
      presignedByName,
      concurrency: 5,
      signal,
      onProgress: pct => dispatch({ type: 'S3', key, pct }),
    })
    if (failed.length)
      throw new Error(`${failed.length} tuile(s) non envoyée(s).`)

    await d.finalize(serverId)
    dispatch({
      type: 'PHASE',
      key,
      phase: 'processing',
      message: 'Assemblage…',
    })
  } catch (err) {
    if (signal?.aborted) return
    dispatch({ type: 'ERROR', key, error: humanizeError(err) })
    if (serverId != null) {
      try {
        await d.abort(serverId)
      } catch {
        /* best effort */
      }
    }
  }
}
